import { useEffect, useState } from "react"

interface ScrollEffects {
  microOpacity: number
  backgroundProgress: number
  ctaVisible: boolean
}

export function useCTAScrollEffects(
  variant: "inline" | "tall-screen" | "mobile",
  externalVisibility?: boolean,
): ScrollEffects {
  const [microOpacity, setMicroOpacity] = useState(1)
  const [backgroundProgress, setBackgroundProgress] = useState(0)
  const [ctaVisible, setCtaVisible] = useState(false)

  // Fade in 0.5s after mount (tall-screen variant)
  useEffect(() => {
    if (variant !== "tall-screen" || externalVisibility !== undefined) return
    const timer = setTimeout(() => setCtaVisible(true), 500)
    return () => clearTimeout(timer)
  }, [variant, externalVisibility])

  // Fade out once the user scrolls (tall-screen variant)
  useEffect(() => {
    if (variant !== "tall-screen" || externalVisibility !== undefined) return
    const handleScroll = () => {
      setCtaVisible(window.scrollY <= 10)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [variant, externalVisibility])

  // Mobile: Hide microcopy immediately when user scrolls from top
  useEffect(() => {
    if (variant !== "mobile") return
    if (typeof window === "undefined") return

    const handleScroll = () => {
      const scrollY = window.scrollY || 0
      setMicroOpacity(scrollY > 0 ? 0 : 1)
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [variant])

  // Mobile: Snap button to new look quickly when scrolling
  useEffect(() => {
    if (variant !== "mobile") return
    if (typeof window === "undefined") return

    const TRANSITION_THRESHOLD = 20 // px

    const handleScroll = () => {
      const scrollY = window.scrollY || 0
      setBackgroundProgress(scrollY > TRANSITION_THRESHOLD ? 1 : 0)
    }

    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [variant])

  return {
    microOpacity,
    backgroundProgress,
    ctaVisible,
  }
}
